import { IConfigurationClinicPostBodyServiceDetailsBody } from "@/api_framework/api_modals/Configuration";
import clsx from "clsx";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown } from "react-icons/fa";

export interface IStepperSelectMenuItemStateKeyValueObject {
  data: any;
}

export interface IStepperSelectMenuItemBasicProperties {
  id: string;
  title: string;
}

export interface IStepperSelectMenuItem extends IStepperSelectMenuItemBasicProperties {
  state?: IStepperSelectMenuItemStateKeyValueObject;
  children?: IStepperSelectMenuItem[];
}

interface IStepperSelectMenuProps {
  options: IStepperSelectMenuItem[];
  defaultSelectedIds: string[];
  showDropdownIcon?: boolean;
  openDropdownByDefault?: boolean;
  onChange?: (options: IConfigurationClinicPostBodyServiceDetailsBody[], selectedIds: string[]) => void;
}

const RenderSelectMenuOption: React.FC<{
  item: IStepperSelectMenuItem;
  selectedItems: string[];
  toggleItem: (item: IStepperSelectMenuItem, isSelected: boolean) => void;
}> = ({ item, selectedItems, toggleItem }) => {
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const isSelected = selectedItems.includes(item.id);
  const hasPartialSelection = item.children?.some(
    (child) =>
      selectedItems.includes(child.id) ||
      (child.children && child.children.some((subChild) => selectedItems.includes(subChild.id))),
  );

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    toggleItem(item, e.target.checked);
  };

  return (
    <div className="py-1">
      <div className="flex justify-start items-center gap-x-2 rounded-md">
        <input
          type="checkbox"
          className={clsx(
            "w-4 h-4",
            {
              "accent-gray-300": hasPartialSelection && item.children && item.children.length > 0,
            },
            {
              "accent-black": hasPartialSelection || !item.children || item.children.length === 0,
            },
          )}
          checked={isSelected}
          onChange={handleCheckboxChange}
        />
        <button
          onClick={(e) => {
            e.preventDefault();
            setShowDropdown(!showDropdown);
          }}
          className="flex justify-between items-center w-full text-gray-700"
        >
          {item.title}
          {item?.children && item?.children?.length > 0 && <FaChevronDown className="w-2 h-2" />}
        </button>
      </div>
      {item.children && (
        <div className="ml-4 text-xs">
          {item.children.map(
            (child) =>
              showDropdown && (
                <RenderSelectMenuOption
                  key={child.id}
                  item={child}
                  selectedItems={selectedItems}
                  toggleItem={toggleItem}
                />
              ),
          )}
        </div>
      )}
    </div>
  );
};

const StepperSelectMenu: React.FC<IStepperSelectMenuProps> = ({ options, defaultSelectedIds, ...props }) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleItem = (item: IStepperSelectMenuItem, isSelected: boolean) => {
    const newSelectedItems = [...selectedItems];

    const updateSelection = (currentItem: IStepperSelectMenuItem, selected: boolean) => {
      if (selected) {
        newSelectedItems.push(currentItem.id);
      } else {
        const index = newSelectedItems.indexOf(currentItem.id);
        if (index > -1) {
          newSelectedItems.splice(index, 1);
        }
      }

      if (currentItem.children) {
        currentItem.children.forEach((child) => updateSelection(child, selected));
      }
    };

    updateSelection(item, isSelected);

    const updateParentSelection = (
      items: IStepperSelectMenuItem[],
      selectedItems: string[],
      parent?: IStepperSelectMenuItem,
    ) => {
      items.forEach((item) => {
        if (item.children) {
          const allChildrenSelected = item.children.every(
            (child) =>
              selectedItems.includes(child.id) ||
              (child.children && child.children.every((subChild) => selectedItems.includes(subChild.id))),
          );

          if (allChildrenSelected) {
            selectedItems.push(item.id);
          } else {
            const index = selectedItems.indexOf(item.id);
            if (index > -1) {
              selectedItems.splice(index, 1);
            }
          }

          updateParentSelection(item.children, selectedItems, item);
        }
      });

      if (parent && items.some((child) => selectedItems.includes(child.id))) {
        selectedItems.push(parent.id);
      }
    };

    updateParentSelection(options, newSelectedItems);
    setSelectedItems(newSelectedItems);
  };

  const renderTitle = (): string => {
    const titles: string[] = [];
    const extractTitles = (item: IStepperSelectMenuItem) => {
      if (selectedItems.includes(item.id)) {
        titles.push(item.title);
      }
      if (item.children) {
        item.children.forEach((child) => extractTitles(child));
      }
    };
    options.forEach((option) => extractTitles(option));
    return titles?.join(", ");
  };

  /* Use Effects */
  useEffect(() => {
    if (props?.openDropdownByDefault) {
      setDropdownOpen(true);
    }
  }, [props?.openDropdownByDefault]);

  const getChildrenItems = (option: IStepperSelectMenuItem) => {
    const childItems = option.children?.map((s) => {
      return s.id;
    });
    return childItems?.flat() ?? [];
  };

  const selectedItemReducer = (selectedItems: string[]) => {
    const newItems: IConfigurationClinicPostBodyServiceDetailsBody[] = [];
    options.forEach((option) => {
      if (selectedItems.includes(option.id)) {
        const childrenItems = getChildrenItems(option);
        const selectedChildren = selectedItems?.filter((itemId) => childrenItems.includes(itemId));
        newItems.push({
          service_id: option.id,
          sub_services_data: selectedChildren.map((item) => {
            return { sub_service_id: item };
          }),
        });
      }
    });
    return newItems;
  };

  useEffect(() => {
    if (props?.onChange) {
      props?.onChange(selectedItemReducer(selectedItems), selectedItems);
    }
  }, [selectedItems]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSelectedItems(defaultSelectedIds);
  }, [defaultSelectedIds]);

  return (
    <div ref={dropdownRef} className="relative flex justify-center items-center w-72">
      <button
        onClick={(e) => {
          e.preventDefault();
          toggleDropdown();
        }}
        className="relative flex justify-start items-center border-gray-100 px-2 py-2 border rounded-md w-72 w-full text-xs cursor-pointer overflow-hidden"
      >
        <input
          type="text"
          readOnly
          placeholder="Select Items"
          value={renderTitle()}
          className="border-none focus:border-none ring-0 focus:ring-0 w-full cursor-pointer outline-none focus:outline-none focus-within:outline-none"
        />
        {props?.showDropdownIcon && (
          <span className="top-1/2 right-0 absolute bg-white p-2 -translate-y-1/2">
            <FaChevronDown className="w-4 h-4 text-gray-600" />
          </span>
        )}
      </button>
      {dropdownOpen && (
        <motion.div
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.05 }}
          className="top-12 z-20 absolute border-gray-200 bg-white px-2 py-2 border rounded-md w-full h-60 overflow-y-scroll"
        >
          {options?.map((option) => (
            <RenderSelectMenuOption
              key={option.id}
              item={option}
              selectedItems={selectedItems}
              toggleItem={toggleItem}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default StepperSelectMenu;
